import React, { useEffect, useState, useRef } from 'react';
import { fetchAPI } from '../../../../utils/utils';
import { can } from '../../../../utils/permissions';
import { getCastellerDisplayName } from '../../../../utils/castellerName';

function MissatgesProva({ castellersInfo, event, castell, versio, socket, userInfo }) {
    const [missatgesProva, setMissatgesProva] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const messagesEndRef = useRef(null);
    const [showMessages, setShowMessages] = useState(false);

    const displayName = id => id in castellersInfo ?
        getCastellerDisplayName(castellersInfo[id])
        : `#${id}`;

    const handleNewMessages = (messages) => {
        // Handle potential API errors returning null
        const msgs = Array.isArray(messages) ? messages : [];
        setMissatgesProva(msgs);
        // If we receive new messages and there's more than one, open the chat automatically
        if (msgs.length > 0) {
            setShowMessages(true);
        }
    };

    // Scroll to bottom when messages change or when chat is opened
    useEffect(() => {
        if (showMessages && messagesEndRef.current) {
            // Use scrollIntoView only for the messages container, not the whole page
            const messagesContainer = messagesEndRef.current.parentElement;
            if (messagesContainer) {
                messagesContainer.scrollTop = messagesContainer.scrollHeight;
            }
        }
    }, [missatgesProva?.length, showMessages]);

    // Fetch messages when component mounts or when prova changes
    useEffect(() => {
        if (!event || !castell || !versio) return;
        
        fetchAPI(`/missatges/${event}/${castell}/${versio}`, handleNewMessages);
        
        // Set up socket listener for real-time updates
        const handleNewMessage = (message) => {
            if (message.event === parseInt(event) && 
                message.castell === castell && 
                message.versio === versio) {
                setMissatgesProva(prev => [...prev, message]);
                // Open chat when a new message arrives
                setShowMessages(true);
            }
        };
        
        socket.on('.new_message', handleNewMessage);
        
        return () => {
            socket.off('.new_message', handleNewMessage);
        };
    }, [event, castell, versio, socket]);

    const sendMessage = (e) => {
        // Prevent default to avoid page scroll
        if (e) e.preventDefault();
        
        if (!inputMessage.trim()) return;
        
        socket.emit('.send_messages', [{
            user: userInfo.id,
            content: inputMessage,
            castell: castell,
            event: event,
            versio: versio,
        }]);
        
        setInputMessage('');

        // Optimistically add message to UI
        setMissatgesProva([...missatgesProva, {
            user: userInfo.id,
            content: inputMessage,
            id: `temp-${Date.now()}`, // Temporary ID until server responds
        }]);

        // Fetch updated messages
        fetchAPI(`/missatges/${event}/${castell}/${versio}`, handleNewMessages, false, true);
    };

    const confirmDeleteMessage = (item, e) => {
        // Prevent default to avoid page scroll
        if (e) e.preventDefault();
        e.stopPropagation();
        
        if (window.confirm('Estàs segur que vols esborrar aquest missatge?')) {
            socket.emit('.delete_message', item);

            // Optimistically remove message from UI
            setMissatgesProva(missatgesProva.filter(message => message.id !== item.id));
            
            // Fetch updated messages
            fetchAPI(`/missatges/${event}/${castell}/${versio}`, handleNewMessages, false, true);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const toggleMessages = (e) => {
        // Prevent default to avoid page scroll
        if (e) e.preventDefault();
        setShowMessages(!showMessages);
    };

    return (
        <div className="missatges-prova">
            <div 
                className="chat-header" 
                onClick={toggleMessages}
                onMouseDown={(e) => e.preventDefault()} // Prevent focus which can cause scroll
            >
                <h3>
                    <span className="chat-icon">💬</span> 
                    Xat {missatgesProva.length > 0 && <span className="message-count">{missatgesProva.length}</span>}
                    <span className={`toggle-icon ${showMessages ? 'open' : ''}`}>▼</span>
                </h3>
            </div>
            
            {showMessages && (
                <>
                    <div className="messages-container">
                        {missatgesProva.length === 0 ? (
                            <div className="no-messages">
                                <p>No hi ha missatges. Sigues el primer en escriure!</p>
                            </div>
                        ) : (
                            missatgesProva.map((missatge) => {
                                const isCurrentUser = parseInt(missatge.user) === parseInt(userInfo.id);
                                return (
                                    <div 
                                        key={`missatge-${missatge.id}-${versio}`}
                                        className={`message-item ${isCurrentUser ? 'current-user' : ''}`}
                                        onContextMenu={(e) => {
                                            if (isCurrentUser || can(userInfo, 'DELETE_PROVA_MESSAGE')) {
                                                confirmDeleteMessage(missatge, e);
                                            }
                                        }}
                                    >
                                        <div className="message-sender">
                                            {displayName(missatge.user)}
                                        </div>
                                        <div className="message-content">
                                            {missatge.content}
                                        </div>
                                        {(isCurrentUser || can(userInfo, 'DELETE_PROVA_MESSAGE')) && (
                                            <div 
                                                className="delete-icon" 
                                                onClick={(e) => confirmDeleteMessage(missatge, e)}
                                                onMouseDown={(e) => e.preventDefault()} // Prevent focus
                                                title="Eliminar missatge"
                                            >
                                                🗑️
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                    <form 
                        className="message-input-container" 
                        onSubmit={sendMessage}
                    >
                        <textarea
                            className="message-input"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Escriu un missatge..."
                            rows={1}
                        />
                        <button 
                            type="submit"
                            className="send-button"
                            onMouseDown={(e) => e.preventDefault()} // Prevent focus
                            disabled={!inputMessage.trim()}
                        >
                            Enviar
                        </button>
                    </form>
                </>
            )}

            <style jsx>{`
                .missatges-prova {
                    margin-top: 0.25rem;
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    border: 1px solid #e0e0e0;
                    max-width: 100%;
                }
                
                .chat-header {
                    background-color: #f0f0f0;
                    padding: 0.4rem 0.75rem;
                    cursor: pointer;
                    border-bottom: ${showMessages ? '1px solid #e0e0e0' : 'none'};
                    transition: background-color 0.2s;
                }
                
                .chat-header:hover {
                    background-color: #e8e8e8;
                }
                
                .chat-header h3 {
                    margin: 0;
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: #333;
                    display: flex;
                    align-items: center;
                }
                
                .chat-icon {
                    margin-right: 0.5rem;
                    font-size: 1rem;
                }
                
                .message-count {
                    background-color: #e0e0e0;
                    color: #333;
                    border-radius: 10px;
                    padding: 0.1rem 0.4rem;
                    font-size: 0.75rem;
                    margin-left: 0.5rem;
                    font-weight: bold;
                }
                
                .toggle-icon {
                    margin-left: auto;
                    transition: transform 0.3s ease;
                    font-size: 0.7rem;
                }
                
                .toggle-icon.open {
                    transform: rotate(180deg);
                }
                
                .messages-container {
                    max-height: 200px;
                    overflow-y: auto;
                    padding: 0.5rem;
                    background-color: white;
                    scrollbar-width: thin;
                }
                
                .messages-container::-webkit-scrollbar {
                    width: 4px;
                }
                
                .messages-container::-webkit-scrollbar-thumb {
                    background-color: #ddd;
                    border-radius: 2px;
                }
                
                .no-messages {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    height: 60px;
                    color: #999;
                    font-style: italic;
                    font-size: 0.9rem;
                }
                
                .message-item {
                    display: flex;
                    flex-direction: column;
                    background-color: white;
                    margin: 0.3rem 0;
                    padding: 0.5rem 0.75rem;
                    border-radius: 8px;
                    border: 1px solid #eee;
                    position: relative;
                    max-width: 90%;
                }
                
                .message-item.current-user {
                    background-color: #f8f8f8;
                    border-color: #eee;
                    margin-left: auto;
                }
                
                .message-sender {
                    color: #666;
                    margin-bottom: 0.3rem;
                    font-weight: 500;
                    font-size: 0.8rem;
                }
                
                .current-user .message-sender {
                    color: #555;
                }
                
                .message-content {
                    color: #333;
                    line-height: 1.3;
                    word-break: break-word;
                    padding-right: 1.5rem;
                    font-size: 0.9rem;
                }
                
                .delete-icon {
                    position: absolute;
                    top: 0.4rem;
                    right: 0.4rem;
                    cursor: pointer;
                    opacity: 0;
                    transition: opacity 0.2s;
                    font-size: 0.8rem;
                }
                
                .message-item:hover .delete-icon {
                    opacity: 0.6;
                }
                
                .delete-icon:hover {
                    opacity: 1 !important;
                }
                
                .message-input-container {
                    display: flex;
                    gap: 0.5rem;
                    padding: 0.5rem;
                    background-color: #f8f8f8;
                    border-top: 1px solid #eee;
                }
                
                .message-input {
                    flex: 1;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    padding: 0.5rem 0.75rem;
                    font-family: inherit;
                    resize: none;
                    font-size: 0.9rem;
                    height: 36px;
                    line-height: 1.2;
                    overflow-y: hidden;
                }
                
                .message-input:focus {
                    outline: none;
                    border-color: #ccc;
                    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05);
                }
                
                .message-input::placeholder {
                    color: #aaa;
                }
                
                .send-button {
                    background-color: #f0f0f0;
                    color: #333;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    padding: 0 1rem;
                    cursor: pointer;
                    font-weight: 500;
                    transition: all 0.2s ease;
                    min-width: 70px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.9rem;
                }
                
                .send-button:hover {
                    background-color: #e8e8e8;
                }
                
                .send-button:active {
                    background-color: #ddd;
                }
                
                .send-button:disabled {
                    background-color: #f8f8f8;
                    color: #bbb;
                    border-color: #eee;
                    cursor: not-allowed;
                }
                
                @media (max-width: 768px) {
                    .missatges-prova {
                        margin-top: 0.5rem;
                    }
                    
                    .messages-container {
                        max-height: 200px;
                    }
                    
                    .message-input {
                        padding: 0.4rem 0.6rem;
                    }
                    
                    .send-button {
                        min-width: 60px;
                        padding: 0 0.6rem;
                    }
                }
            `}</style>
        </div>
    );
}

export default MissatgesProva;
